import { createPortal } from 'react-dom';
import { Camera, Plus, X } from 'lucide-react';

interface LeadCreateChoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Open the standard create-lead form. */
  onCreate: () => void;
  /** Open the AI business card scan flow. */
  onCreateFromPhoto: () => void;
}

/**
 * Small chooser shown when "AI Create from Business Card" is enabled:
 * create a lead manually, or scan a business card photo.
 */
export default function LeadCreateChoiceModal({
  isOpen,
  onClose,
  onCreate,
  onCreateFromPhoto,
}: LeadCreateChoiceModalProps) {
  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 flex items-end sm:items-center justify-center"
      style={{
        zIndex: 10049,
        background: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-sm sm:rounded-2xl rounded-t-2xl shadow-2xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2
            className="text-base font-bold text-gray-900"
            style={{ letterSpacing: '-0.02em' }}
          >
            New Lead
          </h2>
          <button
            onClick={onClose}
            className="h-9 w-9 flex items-center justify-center rounded-full hover:bg-gray-100 active:scale-95 transition-all"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="space-y-3">
          <button
            onClick={onCreate}
            className="w-full flex items-center gap-3 px-4 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-semibold transition-all active:scale-[0.98]"
            style={{ boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)' }}
          >
            <Plus className="h-5 w-5" />
            <span className="flex-1 text-left">Create</span>
          </button>
          <button
            onClick={onCreateFromPhoto}
            className="w-full flex items-center gap-3 px-4 py-4 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-2xl font-semibold transition-all active:scale-[0.98]"
          >
            <Camera className="h-5 w-5" />
            <span className="flex-1 text-left">Create from Photo</span>
            <span className="text-[10px] font-bold uppercase tracking-wide text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
              AI
            </span>
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
